import { BACKEND_URL } from "@/lib/backendUrl";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const KEY = "servecor_theme";
const BACKEND = BACKEND_URL;
export const SYSTEM_BACKDROPS = {
  hope: `${process.env.PUBLIC_URL || ""}/backdrops/hope.jpg`,
  "human-values": `${process.env.PUBLIC_URL || ""}/backdrops/human-values.jpg`,
  "helping-hands": `${process.env.PUBLIC_URL || ""}/backdrops/helping-hands.jpg`,
  himalayas: `${process.env.PUBLIC_URL || ""}/backdrops/himalayas.jpg`,
  waterfall: `${process.env.PUBLIC_URL || ""}/backdrops/waterfall.jpg`,
};
const DEFAULT_BACKDROP = SYSTEM_BACKDROPS.hope;
const ACCENT_VARS = ["--primary", "--primary-foreground", "--ring", "--chart-1",
                     "--accent-foreground"];

const ThemeContext = createContext(null);

function hexToHsl(hex) {
  const clean = (hex || "").replace("#", "");
  if (clean.length !== 6) return null;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(clean.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (!d) return { h: 0, s: 0, l: Math.round(l * 100) };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: Math.round(((h * 60) + 360) % 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function applyAccent(hex, theme) {
  const root = document.documentElement;
  const hsl = hexToHsl(hex);
  if (!hsl) {
    ACCENT_VARS.forEach((v) => root.style.removeProperty(v));
    return;
  }
  const { h, s } = hsl;
  const l = theme === "dark" ? Math.min(80, hsl.l + 9) : hsl.l;
  root.style.setProperty("--primary", `${h} ${s}% ${l}%`);
  root.style.setProperty("--primary-foreground", l > 62 ? "224 39% 12%" : "0 0% 100%");
  root.style.setProperty("--ring", `${h} ${s}% ${Math.min(88, l + 4)}%`);
  root.style.setProperty("--chart-1", `${h} ${s}% ${l}%`);
  root.style.setProperty("--accent-foreground", `${h} ${s}% ${theme === "dark" ? 88 : 40}%`);
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem(KEY) || "dark");
  const [backdrop, setBackdrop] = useState(DEFAULT_BACKDROP);
  const [scrim, setScrim] = useState(0.8);
  const [accent, setAccent] = useState("");
  const [textScale, setTextScale] = useState(
    () => Number(localStorage.getItem("servecor_text_scale")) || 100);

  useEffect(() => {
    document.body.style.zoom = textScale === 100 ? "" : `${textScale}%`;
    document.documentElement.style.setProperty("--vh-zoom", `${100 / (textScale / 100)}vh`);
    localStorage.setItem("servecor_text_scale", String(textScale));
  }, [textScale]);

  const refreshBackdrop = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND}/api/admin/branding`);
      const data = await res.json();
      setBackdrop(data.backdrop_url ? `${BACKEND}${data.backdrop_url}`
        : SYSTEM_BACKDROPS[data.backdrop_choice] || DEFAULT_BACKDROP);
      setScrim(typeof data.backdrop_scrim === "number"
        ? Math.min(100, Math.max(20, data.backdrop_scrim)) / 100 : 0.8);
      setAccent(data.accent || "");
    } catch (e) {
      setBackdrop(DEFAULT_BACKDROP);
    }
  }, []);

  useEffect(() => { refreshBackdrop(); }, [refreshBackdrop]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    root.style.colorScheme = theme;
    root.style.setProperty("--backdrop-image", `url("${backdrop}")`);
    root.style.setProperty("--scrim", String(scrim));
    applyAccent(accent, theme);
    localStorage.setItem(KEY, theme);
  }, [theme, backdrop, scrim, accent]);

  const toggle = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle, backdrop, refreshBackdrop,
                                    accent, setAccent, textScale, setTextScale }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
